/**
 * Brand Context Hook
 *
 * Provides information about the current brand hierarchy context.
 * Use this hook to conditionally render UI elements based on whether
 * the user is on a group, site (point of sale), or central brand.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isGroup, isSite, isCentral, timezone } = useBrandContext();
 *
 *   return (
 *     <>
 *       {isGroup && <GroupOnlyFeature />}
 *       {isSite && <SiteOnlyFeature />}
 *       {isCentral && <CentralOnlyFeature />}
 *       <p>Timezone: {timezone}</p>
 *     </>
 *   );
 * }
 * ```
 */

import { useAuth } from '@/context/AuthContext';
import type { BrandType } from '@bstock/shared';

/** Default timezone fallback if brand has no timezone */
const DEFAULT_TIMEZONE = 'Europe/Paris';

/**
 * Return type for useBrandContext hook
 */
export interface BrandContextValue {
  /** The complete brand object from auth context */
  brand: {
    id: string;
    name: string;
    parentId: string | null;
    type: BrandType;
    role: string;
    timezone: string;
    cutoffHour: number;
  } | null;
  /** The brand ID (shortcut for brand?.id) */
  brandId: string | null;
  /** The parent brand ID (null if this is a group/root brand) */
  parentId: string | null;
  /** The brand timezone (IANA format, e.g., Europe/Paris) */
  timezone: string;
  /** The brand type (GROUP, POINT_OF_SALE, CENTRAL) */
  brandType: BrandType | null;
  /**
   * True if this is a group brand (organizational entity).
   * Groups can see aggregated data from children and manage configuration.
   */
  isGroup: boolean;
  /**
   * True if this is a site brand (physical restaurant).
   * Sites can sync sales and have local data.
   */
  isSite: boolean;
  /**
   * True if this is a central brand (purchasing central / warehouse).
   * Centrals handle procurement and dispatch to sites.
   */
  isCentral: boolean;
}

/**
 * Hook to determine the current brand hierarchy context.
 *
 * Use this hook to conditionally render UI elements:
 * - `isGroup`: Show features reserved for groups (config, aggregated views)
 * - `isSite`: Show features reserved for sites (sales sync, local data)
 * - `isCentral`: Show features reserved for centrals (procurement, dispatch)
 *
 * @returns Brand context information including isGroup, isSite, and isCentral flags
 *
 * @example
 * ```tsx
 * // In Sidebar - hide product management for sites
 * const { isGroup } = useBrandContext();
 * {isGroup && <ProductsMenu />}
 *
 * // In POS Sales - show sync button only for sites
 * const { isSite } = useBrandContext();
 * {isSite && <SyncButton />}
 * ```
 */
export function useBrandContext(): BrandContextValue {
  const { user } = useAuth();
  const brand = user?.currentBrand ?? null;

  const brandType = brand?.type ?? null;
  const isGroup = brandType === 'GROUP';
  const isSite = brandType === 'POINT_OF_SALE';
  const isCentral = brandType === 'CENTRAL';

  return {
    brand,
    brandId: brand?.id ?? null,
    parentId: brand?.parentId ?? null,
    timezone: brand?.timezone ?? DEFAULT_TIMEZONE,
    brandType,
    isGroup,
    isSite,
    isCentral,
  };
}

/**
 * Hook shortcut for getting only the brand timezone.
 * Useful for date formatting hooks and components.
 *
 * @returns The current brand's timezone (IANA format)
 *
 * @example
 * ```tsx
 * const timezone = useBrandTimezone();
 * // 'Europe/Paris' or 'Africa/Casablanca', etc.
 * ```
 */
export function useBrandTimezone(): string {
  const { timezone } = useBrandContext();
  return timezone;
}
